import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import beginHostedCheckout from '@cartwhl/common/api/beginHostedCheckout';
import * as authStore from '@cartwhl/common/stores/authStore';
import * as navigationStore from '@cartwhl/common/stores/navigationStore';
import * as noticesStore from '@cartwhl/common/stores/noticesStore';
import * as productStore from '@cartwhl/common/stores/productStore';
import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';

import { StripePrice } from '../types';
import { getStripeProductID } from '../utils/getStripeProductID';
import handleGenericErrors from '../utils/handleGenericErrors';

type CheckoutStoreState = {
  price: StripePrice | null;
  quantity: number;
};

export const state = proxy<CheckoutStoreState>({
  price: null,
  quantity: 1,
});

if (
  process.env.NODE_ENV === 'development' &&
  typeof window !== 'undefined' &&
  '__REDUX_DEVTOOLS_EXTENSION__' in window
) {
  devtools(state, { name: 'Checkout', enabled: true });
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitWhileLoading(isLoading: () => boolean) {
  while (isLoading()) {
    await sleep(100);
  }
}

async function ensureProductCatalogsLoaded() {
  const fetches: Promise<void>[] = [];

  if (!productStore.state.done && !productStore.state.loading) {
    fetches.push(productStore.fetchProducts());
  }
  if (!updatedProductStore.state.done && !updatedProductStore.state.loading) {
    fetches.push(updatedProductStore.fetchProducts());
  }

  await Promise.all(fetches);
  await waitWhileLoading(() => productStore.state.loading);
  await waitWhileLoading(() => updatedProductStore.state.loading);
}

function resolvePriceForProduct(productId: string): StripePrice | null {
  return (
    updatedProductStore.getPriceByProductAndMode(productId, 'month') ??
    productStore.getPriceByProductAndMode(productId, 'month') ??
    updatedProductStore.getDefaultPriceByProduct(
      updatedProductStore.getProductByID(productId),
    ) ??
    productStore.getDefaultPriceByProduct(
      productStore.getProductByID(productId),
    )
  );
}

///// Actions

export function select(price: StripePrice, quantity: number = 1) {
  state.price = price;
  state.quantity = quantity;
}

export async function fillPrice() {
  const params = new URLSearchParams(window.location.search);
  const priceID = params.get('priceID');
  const productID = params.get('productID');
  const seatsParam = params.get('seats');
  const quantity = parseInt(seatsParam || params.get('quantity') || '1', 10);

  state.quantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;

  await ensureProductCatalogsLoaded();

  if (typeof priceID === 'string') {
    state.price =
      productStore.getPriceByID(priceID) ??
      updatedProductStore.getPriceByID(priceID) ??
      null;
    if (!state.price) {
      noticesStore.error('Unable to find the selected price.');
    }
    return;
  }

  const fetchProductID = productID || getStripeProductID('individual_basic');
  state.price = resolvePriceForProduct(fetchProductID);

  if (!state.price) {
    noticesStore.error(
      'Unable to find a product based on your purchase selection.',
    );
  }
}

export async function startCheckout() {
  const params = new URLSearchParams();

  if (authStore.state.authStatus === 'anonymous') {
    navigationStore.navigate(
      `sign-up?redirectTo=/checkout?${params.toString()}`,
    );
    return state;
  }

  if (state.price) {
    const stripeHostedCheckoutURL = await completeHostedCheckout(
      state.price,
      state.quantity,
    );
    if (stripeHostedCheckoutURL) {
      window.location.href = stripeHostedCheckoutURL;
    }
  } else {
    noticesStore.error(
      'Unable to initiate your purchase. Please refresh and try again.',
    );
  }

  return state;
}

export async function completeHostedCheckout(
  price: StripePrice | null,
  quantity: number,
) {
  if (!price) {
    noticesStore.info('Unexpected error. Please go back and try again.');
    return;
  }

  const { data, error } = await beginHostedCheckout(price, quantity);

  if (error) {
    await handleGenericErrors(error);
  } else if (data?.url) {
    return data.url;
  } else {
    noticesStore.error('Something went wrong, please try again later!');
    return '';
  }
}

///// Helpers

export const formatMoney = (price: StripePrice, quantity: number = 1) => {
  let output = '';
  if (price.currency === 'usd') {
    output += '$';
  }
  if (typeof price.unit_amount === 'number') {
    output += (price.unit_amount * quantity) / 100;
  }
  return output;
};
